'use client';

import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';
import type { Office } from '@jlg-website/database/types';
import type { TestimonialInput } from '@jlg-website/shared/schemas';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Pencil } from 'lucide-react';

const loading = () => <>Loading...</>;

const TestimonialsForm = dynamic(
  () => import('@/components/admin/contents/content-types/testimonials-form'),
  { loading }
);

export function TestimonialsEditDialogForm({
  value,
  onEdit,
}: {
  value: string;
  onEdit: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [offices, setOffices] = useState<Pick<Office, 'id' | 'name'>[]>([]);
  const [defaultValues, setDefaultValues] = useState<TestimonialInput | null>(null);

  const handleSave = () => {
    setOpen(false);
    onEdit();
  };

  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      const [officesRes, testimonialRes] = await Promise.all([
        fetch('/admin/api/offices').then((r) => r.json()),
        fetch(`/admin/api/testimonials/${value}`).then((r) => r.json()),
      ]);
      setOffices(officesRes);
      setDefaultValues(testimonialRes);
    };

    fetchData();
  }, [open, value]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setOffices([]);
          setDefaultValues(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" aria-label="Edit">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      {open && (
        <DialogContent className="lg:min-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Testimonial</DialogTitle>
            {defaultValues && (
              <TestimonialsForm
                offices={offices}
                defaultValues={defaultValues}
                onSave={handleSave}
                customCancelButton={
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                }
              />
            )}
          </DialogHeader>
        </DialogContent>
      )}
    </Dialog>
  );
}

export default TestimonialsEditDialogForm;
